import React, { useState } from 'react';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { InsuranceType, useInsuranceQuery } from './api/useInsuranceQuery';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { LifeInsurance } from './tables/LifeInsurance/LifeInsurance';
import { DisabilityInsurance } from './tables/DisabilityInsurance/DisabilityInsurance';
import { HealthInsurance } from './tables/HealthInsurance/HealthInsurance';
import { LongTermCareInsurance } from './tables/LongTermCareInsurance/LongTermCareInsurance';
import { InsuranceToDelete, InsuranceToUpsert } from './Insurance.meta';
import { DeleteInsuranceModal } from './components/DeleteInsuranceModal/DeleteInsuranceModal';
import { UpsertInsuranceModal } from './components/UpsertInsuranceModal/UpsertInsuranceModal';
import { ProfileTablesLoading } from '../components/ProfileTablesLoading/ProfileTablesLoading';
import { WorkflowIdEnum } from '../../Workflow/api/useWorkflowDataQuery';
import { useInsurance } from './useInsurance';
import { PropertyAndCasualty } from './tables/PropertyAndCasualty/PropertyAndCasualty';

const TablesList = [
  { insuranceType: InsuranceType.Life, Table: LifeInsurance },
  { insuranceType: InsuranceType.Disability, Table: DisabilityInsurance },
  { insuranceType: InsuranceType.LongTermCare, Table: LongTermCareInsurance },
  { insuranceType: InsuranceType.HealthInsurance, Table: HealthInsurance },
  { insuranceType: InsuranceType.PropertyAndCasualty, Table: PropertyAndCasualty },
];

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const Insurance = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const { isSuccess, data } = useInsuranceQuery({ clientId });

  const isEstatePlanningWorkflow = workflowProps
    ? workflowProps.workflowId === WorkflowIdEnum.EstatePlanning
    : false;

  const [insuranceToUpsert, setInsuranceToUpsert] = useState<InsuranceToUpsert | null>(null);

  const isEmpty =
    isSuccess &&
    data &&
    Object.values(isEstatePlanningWorkflow ? { Life: data.Items.Life || [] } : data.Items).every(
      insuranceList => insuranceList.length === 0,
    );

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Insurance"
          description="Please enter the client's insurance policies."
        >
          {isSuccess && !isEmpty && (
            <Button
              leftIcon="fas fa-plus"
              onClick={() =>
                setInsuranceToUpsert({
                  InsuranceType: isEstatePlanningWorkflow ? InsuranceType.Life : undefined,
                })
              }
            >
              Add Insurance
            </Button>
          )}
        </ProfilePageHeader>

        <InsuranceContent
          clientId={clientId}
          workflowProps={workflowProps}
          insuranceToUpsert={insuranceToUpsert}
          setInsuranceToUpsert={setInsuranceToUpsert}
          isEstatePlanningWorkflow={isEstatePlanningWorkflow}
        />
      </Wrapper>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}
    </>
  );
};

interface ContentProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  insuranceToUpsert: InsuranceToUpsert | null;
  setInsuranceToUpsert: (insuranceToUpsert: InsuranceToUpsert | null) => void;
  isEstatePlanningWorkflow: boolean;
}

const InsuranceContent = ({
  clientId,
  workflowProps,
  insuranceToUpsert,
  setInsuranceToUpsert,
  isEstatePlanningWorkflow,
}: ContentProps) => {
  const [insuranceToDelete, setInsuranceToDelete] = useState<InsuranceToDelete | null>(null);

  const { isLoading, isError, refetch, data } = useInsurance({ clientId });

  if (isLoading) {
    return <ProfileTablesLoading isWorkflow={!!workflowProps} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Box display="flex" flexDirection="column" css="gap: 32px">
        {TablesList.map(({ insuranceType, Table }) => {
          if (isEstatePlanningWorkflow && insuranceType !== InsuranceType.Life) {
            return null;
          }

          return (
            <Table
              key={insuranceType}
              data={data}
              setInsuranceToUpsert={setInsuranceToUpsert}
              setInsuranceToDelete={setInsuranceToDelete}
              workflowProps={workflowProps}
            />
          );
        })}
      </Box>

      {insuranceToUpsert && (
        <UpsertInsuranceModal
          clientId={clientId}
          insurance={insuranceToUpsert}
          data={data}
          onClose={() => setInsuranceToUpsert(null)}
          onInsuranceTypeChange={insuranceType =>
            setInsuranceToUpsert({ InsuranceType: insuranceType })
          }
          insuranceToDelete={insuranceToDelete}
          setInsuranceToDelete={setInsuranceToDelete}
          isEstatePlanningWorkflow={isEstatePlanningWorkflow}
        />
      )}

      {insuranceToDelete && (
        <DeleteInsuranceModal
          clientId={clientId}
          insurance={insuranceToDelete}
          onClose={() => setInsuranceToDelete(null)}
          onDelete={() => {
            setInsuranceToDelete(null);
            setInsuranceToUpsert(null);
          }}
        />
      )}
    </>
  );
};
